import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ApiError } from "../api/client";
import { useBotProtection } from "../hooks/useBotProtection";
import { HoneypotField } from "../components/forms/HoneypotField";
import { TurnstileWidget } from "../components/forms/TurnstileWidget";

type Mode = "signin" | "register";

export function SignInPage() {
  const { user, login, register } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, next, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === "signin") {
        await login({ email, password, ...botFields });
      } else {
        await register({ name, email, password, ...botFields });
      }
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Something went wrong");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="section signin-section">
      <div className="signin-card">
        <div className="signin-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signin"}
            className={mode === "signin" ? "active" : ""}
            onClick={() => { setMode("signin"); setError(null); resetBotProtection(); }}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={mode === "register" ? "active" : ""}
            onClick={() => { setMode("register"); setError(null); resetBotProtection(); }}
          >
            Create account
          </button>
        </div>

        <h1 className="signin-title">
          {mode === "signin" ? "Welcome back" : "Join the movement"}
        </h1>
        <p className="signin-sub">
          {mode === "signin"
            ? "Sign in to share your story or manage the night."
            : "An account lets you share testimonies and stay close to the ministry."}
        </p>

        <form onSubmit={onSubmit} className="form-stack" aria-busy={busy}>
          <HoneypotField value={website} onChange={setWebsite} />
          {mode === "register" && (
            <label>
              <span>Your name</span>
              <input
                type="text"
                required
                minLength={1}
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
                autoComplete="name"
              />
            </label>
          )}
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              autoComplete="email"
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              required
              minLength={mode === "register" ? 8 : 1}
              maxLength={200}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
            />
          </label>

          {needsTurnstile && config?.turnstileSiteKey && (
            <TurnstileWidget
              siteKey={config.turnstileSiteKey}
              onToken={onTurnstileToken}
              onExpire={onTurnstileExpire}
              onRegisterReset={registerTurnstileReset}
            />
          )}

          {error && (
            <div className="form-error" role="alert">{error}</div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={busy || !ready}>
              {busy
                ? mode === "signin"
                  ? "Signing in…"
                  : "Creating account…"
                : mode === "signin"
                ? "Sign in"
                : "Create account"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
